import AbstractAccountRow from "./AbstractAccountRow.js";
import AndSpecification from "../../../specification/AndSpecification.js";
import ConstantFormulaNode from "../../../keyFigure/formula/ConstantFormulaNode.js";
import SumFormulaNode from "../../../keyFigure/formula/SumFormulaNode.js";

/**
 * Composite report row.
 *
 * @param {row[]} spec.rows - List of child rows
 * @param {boolean} [spec.collapsed=true] - `true` when the whole group is collapsed. `true` by default
 */
class GroupRow extends AbstractAccountRow {
	constructor({ rows = [], collapsed = true } = {}) {
		super(...arguments);
		this._rows = rows;
		this._collapsed = collapsed;

		this._rows.forEach((row) => {
			row.setParent(this);
			row.setReport(this._report);
		});
	}

	setReport(report) {
		super.setReport(report);
		this._rows.forEach((row) => row.setReport(report));
	}

	getRows() {
		return this._rows;
	}

	getAllLeafRows() {
		return this.getAllRows()
			.filter((row) => !row.isGroupRow())
			.sort((a, b) => a.compareTo(b));
	}

	isCollapsed() {
		return this._collapsed;
	}

	setCollapsed(collapsed) {
		this._collapsed = collapsed;
	}

	toggleCollapsed() {
		this._collapsed = !this._collapsed;
	}

	canHaveChildren() {
		return true;
	}

	isGroupRow() {
		return true;
	}

	isEmpty() {
		return this._rows.length === 0;
	}

	accept(visitor) {
		return visitor.visitGroupRow(this);
	}

	asSpecification() {
		const rowsInFormula = this.getRows().filter((row) =>
			row.canBeUsedInFormulas(),
		);

		return new AndSpecification({
			specifications: rowsInFormula.map((row) => row.asSpecification()),
		});
	}

	expandAccordingTo(anotherRow) {
		if (!anotherRow?.isGroupRow()) return;

		this._collapsed = anotherRow.isCollapsed();
	}

	_asBaseFormulaNode() {
		return this._rowsAsFormulaNode(
			this.getRows().filter((row) => row.canBeUsedInFormulas()),
		);
	}

	_rowsAsFormulaNode(rowsInFormula) {
		switch (rowsInFormula.length) {
			case 0: {
				return new ConstantFormulaNode({
					value: 0,
					type: "Amount",
				});
			}
			case 1: {
				return rowsInFormula[0].asFormulaNode();
			}
			default: {
				return new SumFormulaNode({
					operands: rowsInFormula.map((row) => row.asFormulaNode()),
				});
			}
		}
	}

	_serializedProperties() {
		const properties = super._serializedProperties(...arguments);

		properties.children = this._rows.filter((row) => row.canBeCalculated());

		return properties;
	}

	static getType() {
		return "CompositeRow";
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.rows = context.factory.materializeAll(data.children, context);
	}
}

export default GroupRow;
